import { useMemo, useState } from "react";
import {
  CheckCircle2,
  Download,
  Mail,
  OctagonAlert,
  Upload,
  UserPlus,
} from "lucide-react";
import {
  ROLES,
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  type BulkInviteResult,
  type Role,
} from "@ir/types";
import {
  BULK_INVITE_TEMPLATE,
  createInvite,
  createInvitesBulk,
  downloadCsv,
  parseBulkInviteCsv,
} from "@ir/services";
import {
  Alert,
  Badge,
  Button,
  Field,
  Input,
  Modal,
  SegmentedControl,
  Select,
  Textarea,
  useToast,
} from "@ir/ui";

type Mode = "single" | "bulk";

/**
 * Create invites, one at a time or from a pasted CSV.
 *
 * The CSV is validated in the browser first so the admin fixes typos before
 * anything is sent — the server still validates every row, this is only to make
 * mistakes cheap.
 */
export function InviteDialog({
  open,
  onClose,
  onCompleted,
}: {
  open: boolean;
  onClose: () => void;
  onCompleted: () => void;
}) {
  const toast = useToast();
  const [mode, setMode] = useState<Mode>("single");

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("user");
  const [csv, setCsv] = useState("");
  const [saving, setSaving] = useState(false);
  const [bulkResults, setBulkResults] = useState<BulkInviteResult[] | null>(null);

  const parsed = useMemo(() => (mode === "bulk" ? parseBulkInviteCsv(csv) : null), [csv, mode]);

  const reset = () => {
    setEmail("");
    setRole("user");
    setCsv("");
    setBulkResults(null);
    setMode("single");
  };

  const close = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const sendSingle = async () => {
    setSaving(true);
    try {
      await createInvite({ email: email.trim().toLowerCase(), role });
      toast.success("Invite sent", `${email.trim()} has 30 days to accept.`);
      reset();
      onCompleted();
      onClose();
    } catch (error) {
      toast.error("Could not send that invite", error instanceof Error ? error.message : undefined);
    } finally {
      setSaving(false);
    }
  };

  const sendBulk = async () => {
    if (!parsed || parsed.invites.length === 0) return;
    setSaving(true);
    try {
      const results = await createInvitesBulk(
        parsed.invites.map(({ email: address, role: inviteRole }) => ({
          email: address,
          role: inviteRole,
        })),
      );
      setBulkResults(results);

      const failed = results.filter((entry) => !entry.ok).length;
      if (failed === 0) {
        toast.success("Invites sent", `${results.length} people have been invited.`);
      } else {
        toast.warning(
          "Some invites failed",
          `${results.length - failed} sent, ${failed} failed. See the list for details.`,
        );
      }
      onCompleted();
    } catch (error) {
      toast.error("Bulk invite failed", error instanceof Error ? error.message : undefined);
    } finally {
      setSaving(false);
    }
  };

  const onFileChosen = async (file: File | undefined) => {
    if (!file) return;
    setCsv(await file.text());
    setBulkResults(null);
  };

  return (
    <Modal
      open={open}
      onClose={close}
      size="lg"
      title="Invite people"
      description="Invited users get an email link that expires in 30 days and can only be used once."
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={saving}>
            {bulkResults ? "Close" : "Cancel"}
          </Button>
          {!bulkResults &&
            (mode === "single" ? (
              <Button
                icon={UserPlus}
                loading={saving}
                disabled={!email.trim()}
                onClick={() => void sendSingle()}
              >
                Send invite
              </Button>
            ) : (
              <Button
                icon={UserPlus}
                loading={saving}
                disabled={!parsed?.invites.length}
                onClick={() => void sendBulk()}
              >
                Send {parsed?.invites.length ?? 0} invite
                {parsed?.invites.length === 1 ? "" : "s"}
              </Button>
            ))}
        </>
      }
    >
      {bulkResults ? (
        <div className="space-y-3 pt-2">
          <Alert
            tone={bulkResults.every((entry) => entry.ok) ? "success" : "warning"}
            title="Results"
          >
            {bulkResults.filter((entry) => entry.ok).length} of {bulkResults.length} invites
            were sent.
          </Alert>
          <ul className="divide-y divide-line rounded-lg border border-line">
            {bulkResults.map((entry) => (
              <li key={entry.email} className="flex items-start gap-2.5 px-3 py-2.5 text-sm">
                {entry.ok ? (
                  <CheckCircle2
                    size={15}
                    aria-hidden="true"
                    className="mt-0.5 shrink-0 text-success-500"
                  />
                ) : (
                  <OctagonAlert
                    size={15}
                    aria-hidden="true"
                    className="mt-0.5 shrink-0 text-danger-500"
                  />
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-content">{entry.email}</span>
                  {entry.error && (
                    <span className="block text-xs text-danger-600 dark:text-danger-500">
                      {entry.error}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="space-y-5 pt-2">
          <SegmentedControl
            ariaLabel="Invite mode"
            fullWidth
            value={mode}
            onChange={setMode}
            options={[
              { value: "single", label: "One person", icon: Mail },
              { value: "bulk", label: "Bulk from CSV", icon: Upload },
            ]}
          />

          {mode === "single" ? (
            <>
              <Field label="Email address" htmlFor="invite-email" required>
                <Input
                  id="invite-email"
                  type="email"
                  icon={Mail}
                  placeholder="name@insurancerevolution.co.uk"
                  value={email}
                  autoComplete="off"
                  onChange={(event) => setEmail(event.target.value)}
                />
              </Field>
              <Field label="Role" htmlFor="invite-role" hint={ROLE_DESCRIPTIONS[role]}>
                <Select
                  id="invite-role"
                  options={ROLES.map((value) => ({ value, label: ROLE_LABELS[value] }))}
                  value={role}
                  onChange={(event) => setRole(event.target.value as Role)}
                />
              </Field>
            </>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  icon={Download}
                  onClick={() =>
                    downloadCsv("ir-admin-invite-template.csv", BULK_INVITE_TEMPLATE)
                  }
                >
                  Download template
                </Button>
                <label className="inline-flex">
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    className="sr-only"
                    onChange={(event) => void onFileChosen(event.target.files?.[0])}
                  />
                  <span className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-lg border border-line-strong bg-surface px-3 text-sm text-content transition-colors hover:border-gold-400">
                    <Upload size={15} aria-hidden="true" />
                    Choose a file
                  </span>
                </label>
              </div>

              <Field
                label="CSV contents"
                htmlFor="invite-csv"
                hint="One row per person: email,role. Roles are admin, developer, user or read_only."
              >
                <Textarea
                  id="invite-csv"
                  rows={7}
                  className="font-mono text-xs"
                  placeholder={BULK_INVITE_TEMPLATE}
                  value={csv}
                  onChange={(event) => {
                    setCsv(event.target.value);
                    setBulkResults(null);
                  }}
                />
              </Field>

              {parsed && csv.trim() !== "" && (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    <Badge tone="success" icon={CheckCircle2}>
                      {parsed.invites.length} ready
                    </Badge>
                    {parsed.errors.length > 0 && (
                      <Badge tone="danger" icon={OctagonAlert}>
                        {parsed.errors.length} problem
                        {parsed.errors.length === 1 ? "" : "s"}
                      </Badge>
                    )}
                  </div>

                  {parsed.errors.length > 0 && (
                    <Alert tone="danger" title="Fix these rows before sending">
                      <ul className="mt-1 space-y-0.5 text-xs">
                        {parsed.errors.map((problem) => (
                          <li key={`${problem.line}-${problem.message}`}>
                            Line {problem.line}: {problem.message}
                          </li>
                        ))}
                      </ul>
                    </Alert>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
